// The logos of the organisations: static assets, chosen by the domain of a
// card's source. tools/logos/README.md says how they were collected and
// src/assets/logos/manifest.json where each one came from. The map is written
// by hand, one entry per asset, like the icons of Icon.tsx; tests/logos.test.ts
// checks it against the manifest.
import adm from "../assets/logos/adm.png";
import affariRegionali from "../assets/logos/affari-regionali.png";
import agcm from "../assets/logos/agcm.png";
import agenziaEntrate from "../assets/logos/agenzia-entrate.png";
import anac from "../assets/logos/anac.png";
import art from "../assets/logos/art.png";
import camera from "../assets/logos/camera.png";
import cipess from "../assets/logos/cipess.png";
import conferenzaRegioni from "../assets/logos/conferenza-regioni.png";
import difesa from "../assets/logos/difesa.png";
import finanze from "../assets/logos/finanze.png";
import governo from "../assets/logos/governo.png";
import innovazione from "../assets/logos/innovazione.png";
import interno from "../assets/logos/interno.png";
import invitalia from "../assets/logos/invitalia.png";
import ismea from "../assets/logos/ismea.png";
import maeci from "../assets/logos/maeci.png";
import masaf from "../assets/logos/masaf.png";
import mase from "../assets/logos/mase.png";
import mef from "../assets/logos/mef.png";
import mim from "../assets/logos/mim.png";
import mimit from "../assets/logos/mimit.png";
import mit from "../assets/logos/mit.png";
import mur from "../assets/logos/mur.png";
import politicheEuropee from "../assets/logos/politiche-europee.png";
import regioneAbruzzo from "../assets/logos/regione-abruzzo.svg";
import regioneCalabria from "../assets/logos/regione-calabria.png";
import rgs from "../assets/logos/rgs.png";
import senato from "../assets/logos/senato.png";
import statoregioni from "../assets/logos/statoregioni.png";
import turismo from "../assets/logos/turismo.png";

type Logo = { src: string; domains: readonly string[] };

/** Every logo: the asset and the domains it stands for, lowercase and without www. */
export const LOGOS = {
  anac: { src: anac, domains: ["anticorruzione.it"] },
  adm: { src: adm, domains: ["adm.gov.it"] },
  "agenzia-entrate": { src: agenziaEntrate, domains: ["agenziaentrate.gov.it"] },
  agcm: { src: agcm, domains: ["agcm.it"] },
  art: { src: art, domains: ["autorita-trasporti.it"] },
  cipess: { src: cipess, domains: ["programmazioneeconomica.gov.it"] },
  statoregioni: { src: statoregioni, domains: ["statoregioni.it"] },
  "conferenza-regioni": { src: conferenzaRegioni, domains: ["regioni.it"] },
  "affari-regionali": { src: affariRegionali, domains: ["affariregionali.it"] },
  innovazione: { src: innovazione, domains: ["innovazione.gov.it"] },
  "politiche-europee": { src: politicheEuropee, domains: ["politicheeuropee.gov.it"] },
  ismea: { src: ismea, domains: ["ismea.it"] },
  invitalia: { src: invitalia, domains: ["invitalia.it", "ingate.invitalia.it"] },
  maeci: { src: maeci, domains: ["esteri.it"] },
  masaf: { src: masaf, domains: ["politicheagricole.it"] },
  mase: { src: mase, domains: ["mase.gov.it", "minambiente.it"] },
  turismo: { src: turismo, domains: ["ministeroturismo.gov.it"] },
  mef: { src: mef, domains: ["mef.gov.it"] },
  finanze: { src: finanze, domains: ["finanze.gov.it"] },
  rgs: { src: rgs, domains: ["rgs.mef.gov.it"] },
  interno: { src: interno, domains: ["interno.gov.it"] },
  mim: { src: mim, domains: ["miur.gov.it", "mim.gov.it"] },
  mur: { src: mur, domains: ["mur.gov.it"] },
  difesa: { src: difesa, domains: ["difesa.it"] },
  mimit: { src: mimit, domains: ["mimit.gov.it"] },
  mit: { src: mit, domains: ["mit.gov.it"] },
  governo: { src: governo, domains: ["governo.it"] },
  "regione-abruzzo": { src: regioneAbruzzo, domains: ["bura.regione.abruzzo.it", "regione.abruzzo.it"] },
  "regione-calabria": { src: regioneCalabria, domains: ["burc.regione.calabria.it", "regione.calabria.it"] },
  camera: { src: camera, domains: ["camera.it"] },
  senato: { src: senato, domains: ["senato.it"] },
} as const satisfies Record<string, Logo>;

export type LogoSlug = keyof typeof LOGOS;

/** "WWW.Camera.IT" → "camera.it"; nothing → "". */
export function normaliseDomain(domain: string | null | undefined): string {
  const host = (domain ?? "").trim().toLowerCase();
  return host.startsWith("www.") ? host.slice(4) : host;
}

const BY_DOMAIN: ReadonlyMap<string, string> = new Map(
  Object.values<Logo>(LOGOS).flatMap((logo) => logo.domains.map((domain) => [domain, logo.src] as const)),
);

/**
 * The logo of a domain: the exact host first, then each parent host down to
 * two labels (ingate.invitalia.it → invitalia.it), so an exact entry
 * (rgs.mef.gov.it) is never overridden by a parent (mef.gov.it). Undefined
 * when no logo is mapped.
 */
export function logoFor(domain: string | null | undefined): string | undefined {
  let host = normaliseDomain(domain);
  while (host.includes(".")) {
    const src = BY_DOMAIN.get(host);
    if (src !== undefined) return src;
    host = host.slice(host.indexOf(".") + 1);
  }
  return undefined;
}
